import {
  ArrowDown,
  ArrowUpRight,
  BookOpenText,
  CalendarDays,
  Check,
  Clock3,
  GraduationCap,
  Laptop,
  Mail,
  MapPin,
  Phone,
  ScrollText,
  Sparkles,
  UsersRound,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { assetUrl } from "@/lib/assets";

const formationAreas = [
  {
    number: "01",
    title: "Formación bíblica",
    description:
      "Un estudio profundo y responsable de la Palabra que fortalece las convicciones y prepara para enseñarla con claridad.",
    icon: BookOpenText,
  },
  {
    number: "02",
    title: "Formación teológica",
    description:
      "Bases teológicas sólidas para comprender la fe, responder a los desafíos actuales y servir con integridad.",
    icon: ScrollText,
  },
  {
    number: "03",
    title: "Formación ministerial",
    description:
      "Herramientas prácticas para desarrollar el llamado, liderar comunidades saludables y cumplir la Gran Comisión.",
    icon: UsersRound,
  },
];

const requirements = [
  "Carta de recomendación de su pastor",
  "Solicitud de ingreso completa",
  "Copia de la cédula por ambos lados",
  "Dos fotografías tipo pasaporte o selfie con fondo liso",
];

export default function SetadPage() {
  return (
    <div className="acad-react-page acad-setad-page">
      <Header />

      <main>
        <section className="acad-setad-hero" data-nav="dark">
          <div className="acad-setad-hero-media" aria-hidden="true">
            <img src={assetUrl("setad-historic-campus.jpg")} alt="" />
          </div>
          <span className="acad-setad-hero-gradient" aria-hidden="true" />
          <span className="acad-setad-hero-ring" aria-hidden="true" />

          <div className="acad-setad-shell acad-setad-hero-inner">
            <div className="acad-setad-hero-top">
              <img src={assetUrl("setad-logo.png")} alt="SETAD" />
              <span>Seminario Teológico de las Asambleas de Dios</span>
            </div>

            <div className="acad-setad-hero-copy">
              <span className="acad-setad-eyebrow"><Sparkles aria-hidden="true" /> Desde 1952</span>
              <h1>
                Formación que
                <span>responde al llamado.</span>
              </h1>
              <p>
                Preparación bíblica, teológica y ministerial para hombres y mujeres que desean
                servir a Dios con conocimiento, carácter y pasión por la Gran Comisión.
              </p>
            </div>

            <div className="acad-setad-hero-actions">
              <a href="#formacion">Conocer SETAD <ArrowDown aria-hidden="true" /></a>
              <a href="https://www.setadcr.org/" target="_blank" rel="noreferrer">
                Visitar sitio oficial <ArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="acad-setad-purpose" id="formacion">
          <div className="acad-setad-shell acad-setad-purpose-grid">
            <span className="acad-setad-eyebrow acad-setad-eyebrow--dark">Nuestra razón de ser</span>
            <div className="acad-setad-purpose-copy">
              <h2>Equipar obreros para una iglesia que cumple su misión.</h2>
              <div className="acad-setad-mission-grid">
                <article>
                  <small>Misión</small>
                  <p>Formar integralmente obreros que respondan con excelencia a su llamado ministerial.</p>
                </article>
                <article>
                  <small>Visión</small>
                  <p>Ser una institución educativa teológica que forma ministros de las Asambleas de Dios para cumplir la Gran Comisión.</p>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="acad-setad-formation">
          <div className="acad-setad-shell">
            <div className="acad-setad-section-heading">
              <span className="acad-setad-eyebrow acad-setad-eyebrow--dark">Formación integral</span>
              <h2>Conocimiento que se convierte en servicio.</h2>
            </div>

            <div className="acad-setad-card-grid">
              {formationAreas.map((area) => {
                const Icon = area.icon;

                return (
                  <article className="acad-setad-card" key={area.number}>
                    <div><span>{area.number}</span><Icon aria-hidden="true" /></div>
                    <h3>{area.title}</h3>
                    <p>{area.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="acad-setad-history" data-nav="dark">
          <div className="acad-setad-shell acad-setad-history-grid">
            <div className="acad-setad-history-copy">
              <span className="acad-setad-eyebrow">Una historia de formación</span>
              <strong>1952</strong>
              <h2>Más de siete décadas preparando ministros.</h2>
              <p>
                El Instituto Bíblico de las Asambleas de Dios nació en junio de 1952 por iniciativa
                de los misioneros David y Ruth Kesinger. Comenzó con 18 estudiantes en la Iglesia
                Central, La Castellana, San José.
              </p>
              <p>
                Con el tiempo extendió la formación a distintas regiones del país y en 2011 asumió
                el nombre de Seminario Teológico. Hoy combina experiencias presenciales y virtuales.
              </p>
            </div>

            <div className="acad-setad-history-gallery">
              <figure className="acad-setad-history-main">
                <img src={assetUrl("setad-founders.jpg")} alt="David y Ruth Kesinger, fundadores de la institución" loading="lazy" decoding="async" />
                <figcaption>David y Ruth Kesinger · Fundadores</figcaption>
              </figure>
              <figure>
                <img src={assetUrl("setad-first-students.jpg")} alt="Primeras generaciones de estudiantes del instituto bíblico" loading="lazy" decoding="async" />
                <figcaption>Una herencia que continúa</figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="acad-setad-study">
          <div className="acad-setad-shell acad-setad-study-grid">
            <div className="acad-setad-study-panel">
              <span className="acad-setad-eyebrow acad-setad-eyebrow--dark">Estudie con flexibilidad</span>
              <h2>Prepárese desde donde Dios le ha llamado.</h2>
              <p>
                SETAD ofrece alternativas presenciales y virtuales para acompañar distintas etapas,
                ritmos y responsabilidades ministeriales.
              </p>
              <div className="acad-setad-modalities">
                <div><GraduationCap aria-hidden="true" /><span><b>Presencial</b>Comunidad y acompañamiento</span></div>
                <div><Laptop aria-hidden="true" /><span><b>Virtual</b>Acceso flexible a la formación</span></div>
              </div>
            </div>

            <aside className="acad-setad-requirements">
              <div className="acad-setad-requirements-title">
                <CalendarDays aria-hidden="true" />
                <div><small>Proceso de ingreso</small><h3>Requisitos de matrícula</h3></div>
              </div>
              <ul>
                {requirements.map((requirement) => <li key={requirement}><Check aria-hidden="true" />{requirement}</li>)}
              </ul>
              <a href="https://www.setadcr.org/" target="_blank" rel="noreferrer">
                Consultar matrícula <ArrowUpRight aria-hidden="true" />
              </a>
            </aside>
          </div>
        </section>

        <section className="acad-setad-contact" data-nav="dark">
          <div className="acad-setad-shell">
            <div className="acad-setad-contact-heading">
              <span className="acad-setad-eyebrow">Contacto SETAD</span>
              <h2>Dé el siguiente paso en su formación.</h2>
            </div>

            <div className="acad-setad-contact-grid">
              <a href="tel:+50684871278"><Phone aria-hidden="true" /><small>Teléfono</small><strong>+506 8487-1278</strong><ArrowUpRight aria-hidden="true" /></a>
              <a href="mailto:ibadcentralcr@gmail.com"><Mail aria-hidden="true" /><small>Correo</small><strong>ibadcentralcr@gmail.com</strong><ArrowUpRight aria-hidden="true" /></a>
              <a href="https://www.google.com/maps/search/?api=1&query=500+metros+norte+y+75+oeste+del+Banco+Nacional+San+Jose+Costa+Rica" target="_blank" rel="noreferrer"><MapPin aria-hidden="true" /><small>Ubicación</small><strong>500 m norte y 75 oeste del Banco Nacional, San José</strong><ArrowUpRight aria-hidden="true" /></a>
              <div><Clock3 aria-hidden="true" /><small>Horario</small><strong>Lunes a viernes · 8:00 a.m.–12:00 p.m. y 1:00 p.m.–5:00 p.m.</strong></div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
